import { useMemo } from 'react';
import {
  CheckSquareOffset,
  DotsThree,
  DownloadSimple,
  EnvelopeSimple,
  Export,
  Trash,
  User,
  Waves,
  Wrench,
} from '@phosphor-icons/react';
import { Button, Dropdown, Flex, Spin, Tag, Typography, theme as antTheme } from 'antd';
import styled from 'styled-components';
import { HeaderBackButton } from '../../../components';
import { theme } from '../../../utility';
import { WaveStatusRenderer } from '../common';

const { useToken } = antTheme;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
`;

/** AppHeader left row: back, wave number, status, warehouse, allocation summary. */
export function WaveDetailHeaderLead({ data, summary, loading }) {
  const { token } = useToken();
  const waveNumber = data?.waveNumber;
  const status = data?.status;
  const warehouseName = data?.warehouseId?.name;
  const totalAllocations = summary?.totalAllocations;
  const pendingCount = summary?.allocationsByStatus?.pending;

  return (
    <Flex
      align="center"
      gap={12}
      wrap="nowrap"
      style={{ minWidth: 0, maxWidth: '100%', height: '100%' }}
    >
      <HeaderBackButton />
      {loading ? (
        <Spin size="small" />
      ) : waveNumber || status || warehouseName || totalAllocations ? (
        <Flex align="center" gap={8} style={{ minWidth: 0 }} wrap="nowrap">
          {waveNumber ? (
            <Flex align="center" gap={6} style={{ minWidth: 0 }}>
              <IconWrap>
                <Waves size={18} color={token.colorText} />
              </IconWrap>
              <Typography.Text
                strong
                style={{
                  fontSize: 14,
                  lineHeight: 1,
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {waveNumber}
              </Typography.Text>
            </Flex>
          ) : null}

          {status ? <WaveStatusRenderer status={status} /> : null}

          {warehouseName ? <Tag color={theme.colors.grey}>{warehouseName}</Tag> : null}

          {totalAllocations ? (
            <Tag mini color={theme.colors.grey}>
              {totalAllocations} Items, {pendingCount || 0} Pending
            </Tag>
          ) : null}
        </Flex>
      ) : null}
    </Flex>
  );
}

/** AppHeader right-side actions for wave detail (three-dot menu). */
export function WaveDetailHeaderActions({
  loading,
  isWaveInBuilding,
  isWaveInReady,
  markWaveReady,
  moveWaveToBuilding,
  assignToUser,
  deleteWave,
  canSendEmail,
  generateEmail,
  emailGenerating,
  generateDocument,
  documentGenerating,
  exportCSV,
  exporting,
}) {
  const menuItems = useMemo(() => {
    const items = [];

    if (isWaveInBuilding) {
      items.push({
        key: 'mark-ready',
        label: 'Mark Wave Ready',
        icon: <CheckSquareOffset size={16} />,
        disabled: loading,
        onClick: markWaveReady,
      });
    }

    if (isWaveInReady) {
      items.push({
        key: 'move-to-building',
        label: 'Move to Building',
        icon: <Wrench size={16} />,
        disabled: loading,
        onClick: moveWaveToBuilding,
      });
    }

    items.push(
      {
        key: 'assign-user',
        label: 'Assign to User',
        icon: <User size={16} />,
        disabled: loading,
        onClick: assignToUser,
      },
      {
        key: 'delete',
        label: 'Delete Wave',
        icon: <Trash size={16} />,
        danger: true,
        disabled: loading,
        onClick: deleteWave,
      },
    );

    if (canSendEmail) {
      items.push({
        key: 'mail-document',
        label: 'Mail Document',
        icon: <EnvelopeSimple size={16} />,
        disabled: emailGenerating,
        onClick: generateEmail,
      });
    }

    items.push(
      {
        key: 'download-batch',
        label: 'Downlaod Batch',
        icon: <DownloadSimple size={16} />,
        disabled: documentGenerating,
        onClick: generateDocument,
      },
      {
        key: 'export',
        label: 'Export',
        icon: <Export size={16} />,
        disabled: exporting,
        onClick: exportCSV,
      },
    );

    return items;
  }, [
    isWaveInBuilding,
    isWaveInReady,
    loading,
    markWaveReady,
    moveWaveToBuilding,
    assignToUser,
    deleteWave,
    canSendEmail,
    generateEmail,
    emailGenerating,
    generateDocument,
    documentGenerating,
    exportCSV,
    exporting,
  ]);

  const anyLoading = loading || emailGenerating || documentGenerating || exporting;

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click', 'hover']}
      placement="bottomRight"
    >
      <Button
        icon={<DotsThree size={18} weight="bold" />}
        loading={anyLoading}
        aria-label="Wave actions"
      />
    </Dropdown>
  );
}
